import { useEffect, useMemo, useRef, type KeyboardEvent } from "react";
import { FileText, Lock, X } from "lucide-react";
import { Button } from "@/design-system/components/ui";
import { cn } from "@/design-system/lib/utils";
import { resolvePreviewKind } from "@/design-system/lib/preview";
// Dates go through the PURE formatters with an EXPLICIT null zone, never the
// staff date-formatter hook in `@/utils/datetime` — that hook reads the staff
// AuthContext, which this tree never mounts. See portal/isolation.test.ts
// (FORBIDDEN_PREFIXES) and portal/portalInstantsRenderInOneZone.test.tsx.
import { formatDateInTz } from "@/design-system/lib/format";
import type { PortalMaterialDto } from "../api/types";

interface MaterialPreviewDrawerProps {
  material: PortalMaterialDto | null;
  open: boolean;
  gated: boolean;
  gateMessage: string;
  previewUrl: string | null;
  onClose: () => void;
  onDownload: (id: string) => void;
}

const UNSUPPORTED_MESSAGE = "Preview not available for this file type — download to view.";

export function MaterialPreviewDrawer({
  material,
  open,
  gated,
  gateMessage,
  previewUrl,
  onClose,
  onDownload,
}: MaterialPreviewDrawerProps) {
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  // Office files (.docx/.xlsx/.pptx, …) are converted to PDF by the preview
  // endpoint, so they render in the same iframe as native PDFs.
  const previewKind = useMemo(() => {
    if (!material) return "unsupported" as const;
    const kind = resolvePreviewKind({
      contentType: material.contentType,
      fileName: material.originalFileName,
    });
    if (kind === "pdf" || kind === "office") return "pdf" as const;
    if (kind === "image") return "image" as const;
    return "unsupported" as const;
  }, [material]);

  useEffect(() => {
    if (!open) return;
    previouslyFocusedRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    document.body.style.overflow = "hidden";
    window.setTimeout(() => closeButtonRef.current?.focus(), 0);

    const handler = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => {
      document.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
      previouslyFocusedRef.current?.focus();
    };
  }, [open, onClose]);

  if (!open || !material) return null;

  const subtitleParts = [
    formatBytes(material.sizeBytes),
    formatDateInTz(material.createdAt, null),
    material.originalFileName,
  ].filter(Boolean);

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-onyx/45 backdrop-blur-[1px]"
      role="presentation"
      onClick={onClose}
      data-testid="material-preview-overlay"
    >
      <aside
        ref={drawerRef}
        className={cn(
          "h-full w-full max-w-4xl bg-white-pure shadow-lg border-l-[2px] border-l-gold",
          "flex flex-col animate-slide-in-right",
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby="material-preview-title"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={trapFocus}
        data-testid="material-preview-drawer"
      >
        <header className="flex items-start justify-between gap-space-3 p-space-4 border-b border-border-light">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-space-2">
              <h2
                id="material-preview-title"
                className="font-display font-bold text-h3 text-onyx tracking-normal break-words m-0"
              >
                {material.title}
              </h2>
              <span className="inline-flex items-center rounded-pill bg-gold-highlight/20 px-space-2 py-0.5 text-caption font-semibold uppercase tracking-label text-gold-shadow">
                {material.category ?? "General"}
              </span>
            </div>
            <p className="text-caption text-gray-mid mt-space-1 m-0">
              {subtitleParts.join(" · ")}
            </p>
            {material.description && (
              <p className="text-small text-onyx mt-space-2 mb-0 max-w-3xl">
                {material.description}
              </p>
            )}
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className="shrink-0 p-space-1 rounded-full hover:bg-white-warm focus-visible:outline-none focus-visible:shadow-goldRing"
            aria-label="Close preview"
            data-testid="material-preview-close"
          >
            <X size={18} aria-hidden />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto bg-white-warm/35 p-space-4">
          {gated ? (
            <LockedPreview message={gateMessage} />
          ) : previewKind === "pdf" && previewUrl ? (
            <iframe
              title={`Preview of ${material.title}`}
              src={previewUrl}
              className="h-full min-h-[50vh] w-full rounded-md border border-border-light bg-white-pure shadow-sm"
              data-testid="material-preview-pdf"
            />
          ) : previewKind === "image" && previewUrl ? (
            <div className="flex min-h-[50vh] items-center justify-center rounded-md border border-border-light bg-white-pure p-space-4">
              <img
                src={previewUrl}
                alt={`Preview of ${material.title}`}
                className="max-h-[72vh] max-w-full rounded-sm object-contain shadow-sm"
                data-testid="material-preview-image"
              />
            </div>
          ) : (
            <UnsupportedPreview material={material} />
          )}
        </div>

        <footer className="flex items-center justify-end gap-space-2 p-space-4 border-t border-border-light bg-white-pure">
          <Button type="button" variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button
            type="button"
            onClick={() => onDownload(material.id)}
            disabled={gated}
            data-testid="material-preview-download"
          >
            {gated ? "Locked" : "Download"}
          </Button>
        </footer>
      </aside>
    </div>
  );
}

function LockedPreview({ message }: { message: string }) {
  return (
    <div className="flex min-h-[45vh] flex-col items-center justify-center gap-space-3 rounded-md border border-border-light bg-white-pure p-space-6 text-center">
      <Lock size={28} className="text-gold-shadow" aria-hidden />
      <p className="m-0 max-w-md text-small text-onyx" data-testid="material-preview-locked">
        {message}
      </p>
    </div>
  );
}

function UnsupportedPreview({ material }: { material: PortalMaterialDto }) {
  return (
    <div className="flex min-h-[45vh] flex-col items-center justify-center gap-space-3 rounded-md border border-border-light bg-white-pure p-space-6 text-center">
      <FileText size={32} className="text-gold-shadow" aria-hidden />
      <div>
        <p className="m-0 text-small font-semibold text-onyx">{material.originalFileName}</p>
        <p className="m-0 mt-space-1 text-caption text-gray-mid">{material.contentType}</p>
      </div>
      <p className="m-0 max-w-md text-small text-gray-mid" data-testid="material-preview-unsupported">
        {UNSUPPORTED_MESSAGE}
      </p>
    </div>
  );
}

function trapFocus(e: KeyboardEvent<HTMLElement>) {
  if (e.key !== "Tab") return;
  const root = e.currentTarget;
  const focusable = getFocusableElements(root);
  if (focusable.length === 0) return;

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const active = document.activeElement;

  if (e.shiftKey && active === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && active === last) {
    e.preventDefault();
    first.focus();
  }
}

function getFocusableElements(root: HTMLElement): HTMLElement[] {
  return Array.from(
    root.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), iframe, input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((el) => !el.hasAttribute("disabled") && el.getAttribute("aria-hidden") !== "true");
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export type { MaterialPreviewDrawerProps };
export { UNSUPPORTED_MESSAGE };
